import { type FrameToHost, type HostToFrame, isOddinMessage, PROTOCOL_VERSION } from './protocol';

export interface MountEmbedOptions {
  /** Element to mount the iframe into. */
  container: HTMLElement;
  /** URL of the hosted embed page, e.g. `https://player-dev.oddin-video.gg/embed/`. */
  src: string;
  /**
   * API base URL, forwarded to the embed page as a query parameter.
   *
   * ⚠️ QoE beacons: the beacons endpoint is derived from this host by
   * convention (`feed[-dev].<domain>` → `beacons[-dev].<domain>`). If your
   * base is NOT a feed host, nothing derives and analytics is off — and
   * `mountEmbed` CANNOT fix it, because the only way to name an endpoint is
   * {@link EmbedConfig.analytics}, which the embed page reads from injected
   * config and deliberately never from the query string (a link must not be
   * able to redirect a session's telemetry). Host the embed page yourself
   * and inject `window.HAVIK_EMBED_CONFIG` with `analytics: { endpoint }`.
   */
  baseUrl: string;
  matchUrn: string;
  /**
   * DEV/DEMO ONLY: appended as a query param so the embed page can read it.
   * In production the embed page is hosted by you and injects the key
   * server-side — do not pass it here.
   */
  apiKey?: string;
  /** Origin of the embed page used to verify its messages. Defaults to new URL(src).origin. */
  allowedFrameOrigin?: string;
  muted?: boolean;
  /**
   * iframe sandbox attribute. Default keeps playback + EME + fullscreen working
   * while blocking top-frame navigation and popups. Override only if you know
   * what you're doing.
   */
  sandbox?: string;
  /** Convenience callback for every event from the iframe. */
  onEvent?: (msg: FrameToHost) => void;
}

export interface EmbedHandle {
  readonly iframe: HTMLIFrameElement;
  play(): void;
  pause(): void;
  setMuted(muted: boolean): void;
  setVolume(volume: number): void;
  setQuality(index: number): void; // -1 = auto
  setMaxBitrate(bitrate: number | null): void;
  setAudioTrack(id: number): void;
  setTextTrack(id: number): void; // -1 = off
  seekToLive(): void;
  enterPip(): void;
  exitPip(): void;
  retry(): void;
  load(matchUrn: string): void;
  on(cb: (msg: FrameToHost) => void): () => void;
  destroy(): void;
}

/**
 * Mode C — mount the hosted iframe player and bridge it over postMessage. Both
 * ends verify event.origin on every message; commands are posted only to the
 * embed page's origin.
 */
export function mountEmbed(opts: MountEmbedOptions): EmbedHandle {
  if (!opts.container || typeof opts.container.appendChild !== 'function') {
    throw new Error('mountEmbed: opts.container must be a DOM element');
  }
  if (!opts.src) throw new Error('mountEmbed: opts.src is required');
  if (!opts.baseUrl || !opts.matchUrn) {
    throw new Error('mountEmbed: opts.baseUrl and opts.matchUrn are required');
  }
  const url = new URL(opts.src, location.href);
  const frameOrigin = opts.allowedFrameOrigin ?? url.origin;

  url.searchParams.set('baseUrl', opts.baseUrl);
  url.searchParams.set('matchUrn', opts.matchUrn);
  url.searchParams.set('parentOrigin', location.origin);
  if (opts.muted != null) url.searchParams.set('muted', String(opts.muted));
  if (opts.apiKey) {
    url.searchParams.set('apiKey', opts.apiKey);
    console.warn(
      'havik-player: passing apiKey to mountEmbed places it in the iframe URL — DEV/DEMO only. ' +
        'In production inject it server-side into window.HAVIK_EMBED_CONFIG on the embed page.',
    );
  }

  const iframe = document.createElement('iframe');
  iframe.src = url.toString();
  iframe.allow = 'autoplay; encrypted-media; fullscreen';
  iframe.setAttribute('allowfullscreen', '');
  // Keep any api-key-bearing URL out of Referer, and sandbox the embed so a
  // compromised embed page can't navigate the top frame or open popups.
  // allow-same-origin is required for EME/MediaKeys and the embed's own storage.
  iframe.referrerPolicy = 'no-referrer';
  iframe.setAttribute(
    'sandbox',
    opts.sandbox ?? 'allow-scripts allow-same-origin allow-presentation',
  );
  iframe.style.cssText = 'border:0;width:100%;height:100%;display:block';
  opts.container.appendChild(iframe);

  const listeners = new Set<(msg: FrameToHost) => void>();

  const onMessage = (ev: MessageEvent) => {
    if (ev.source !== iframe.contentWindow) return;
    if (ev.origin !== frameOrigin) return;
    if (!isOddinMessage(ev.data)) return;
    const msg = ev.data as FrameToHost;
    if (msg.type === 'oddin:ready' && msg.protocol !== PROTOCOL_VERSION) {
      console.warn(
        `havik-player: embed protocol v${msg.protocol} != host v${PROTOCOL_VERSION}. ` +
          'Pin the embed page version to the SDK version to avoid skew.',
      );
    }
    opts.onEvent?.(msg);
    listeners.forEach((cb) => cb(msg));
  };
  window.addEventListener('message', onMessage);

  const send = (msg: HostToFrame) => iframe.contentWindow?.postMessage(msg, frameOrigin);

  return {
    iframe,
    play: () => send({ type: 'oddin:play' }),
    pause: () => send({ type: 'oddin:pause' }),
    setMuted: (muted) => send({ type: 'oddin:setMuted', muted }),
    setVolume: (volume) => send({ type: 'oddin:setVolume', volume }),
    setQuality: (index) => send({ type: 'oddin:setQuality', index }),
    setMaxBitrate: (bitrate) => send({ type: 'oddin:setMaxBitrate', bitrate }),
    setAudioTrack: (id) => send({ type: 'oddin:setAudioTrack', id }),
    setTextTrack: (id) => send({ type: 'oddin:setTextTrack', id }),
    seekToLive: () => send({ type: 'oddin:seekToLive' }),
    enterPip: () => send({ type: 'oddin:enterPip' }),
    exitPip: () => send({ type: 'oddin:exitPip' }),
    retry: () => send({ type: 'oddin:retry' }),
    load: (matchUrn) => send({ type: 'oddin:load', matchUrn }),
    on: (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    destroy: () => {
      send({ type: 'oddin:destroy' });
      window.removeEventListener('message', onMessage);
      iframe.remove();
    },
  };
}
